import React from 'react';
import { actionFor } from './StoryOverlay';
import { tintFor } from '../../utils/tints';

/**
 * What a guest can do with a Portal in the viewer (handoff v3 §7): one compact row over a
 * 16% scrim, in the bottom 13% the composer keeps clear — the in-count chip (two faces and
 * `4 IN`, into the Portal page), the reply field, and I'M DOWN. After joining it reads YOU'RE
 * IN on cyan and taps straight through to the page.
 *
 * The host and someone already in or waiting get their state where I'M DOWN would be; the chip
 * and VIEW PORTAL take everyone to the page.
 */
const pillBase = { height: 44, borderRadius: 999, fontFamily: 'inherit', fontWeight: 700, letterSpacing: '0.1em', border: 0, cursor: 'pointer', transition: 'background-color .2s, color .2s' };

function Faces({ people = [], name }) {
  const faces = people.slice(0, 2);
  while (faces.length < 2) faces.push(null);
  return (
    <span style={{ display: 'flex' }} aria-hidden>
      {faces.map((p, i) => (
        <span
          key={i}
          style={{ width: 28, height: 28, marginLeft: i ? -10 : 0, borderRadius: '50%', border: '2px solid #000', boxSizing: 'border-box', overflow: 'hidden', background: p?.avatarUrl ? '#1A1A1A' : tintFor(p?.userId || `${name}${i}`) }}
        >
          {p?.avatarUrl && <img src={p.avatarUrl} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />}
        </span>
      ))}
    </span>
  );
}

export default function PortalActions({
  portal,
  isOwn = false,
  busy = false,
  joined = false,          // this session's I'M DOWN went through
  requested = false,       // this session's ASK TO JOIN went through
  onAction,
  onViewPortal,
  replyValue = '',
  onReplyChange = null,
  onReplySend = null,
  onReplyFocus = null,      // (true|false) — typing a reply pauses the story
  replyTo = '',
}) {
  if (!portal) return null;
  const action = actionFor(joined ? 'accepted' : requested ? 'applied_pending' : portal.viewerState, isOwn, portal.mode);
  const count = (portal.acceptedCount ?? 0) + (joined && portal.viewerState !== 'accepted' ? 1 : 0);
  const canJoin = !action.done;
  const done = !canJoin;

  let button;
  if (canJoin) {
    button = (
      <button type="button" disabled={busy} onClick={onAction} className="pr-story-action disabled:opacity-60" style={{ ...pillBase, flexShrink: 0, padding: '0 16px', background: '#fff', color: '#000', fontSize: 12, whiteSpace: 'nowrap' }}>
        {busy ? '…' : action.label}
      </button>
    );
  } else if (action.requested) {
    // Waiting on the host: the button says so and does nothing.
    button = (
      <span data-testid="portal-state" style={{ ...pillBase, flexShrink: 0, display: 'flex', alignItems: 'center', padding: '0 16px', background: 'rgba(255,255,255,.18)', color: '#fff', fontSize: 12, whiteSpace: 'nowrap', cursor: 'default' }}>
        {action.label}
      </span>
    );
  } else if (action.inverted) {
    button = (
      <button type="button" onClick={onViewPortal} data-testid="portal-state" style={{ ...pillBase, flexShrink: 0, padding: '0 16px', background: '#00FFFF', color: '#000', fontSize: 12, whiteSpace: 'nowrap' }}>
        {action.label}
      </button>
    );
  } else {
    button = (
      <button type="button" onClick={onViewPortal} className="pr-story-action" style={{ ...pillBase, flexShrink: 0, padding: '0 16px', background: '#fff', color: '#000', fontSize: 12, whiteSpace: 'nowrap' }}>
        {action.label}
      </button>
    );
  }

  return (
    <>
      <div aria-hidden className="pointer-events-none absolute inset-x-0 bottom-0" style={{ height: '16%', background: 'linear-gradient(to bottom, rgba(0,0,0,0), rgba(0,0,0,.8))', zIndex: 4 }} />
      <div className="absolute flex items-center font-mono text-white" style={{ left: 12, right: 12, bottom: 'calc(16px + env(safe-area-inset-bottom, 0px))', gap: 8, zIndex: 5 }} data-testid="portal-actions">
        <button
          type="button"
          onClick={onViewPortal}
          aria-label={`View portal · ${count} in`}
          style={{ ...pillBase, flexShrink: 0, display: 'flex', alignItems: 'center', gap: 6, padding: '0 12px 0 4px', background: 'rgba(0,0,0,.72)', color: '#fff' }}
        >
          <Faces people={portal.acceptedPreview || []} name={portal.id} />
          <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.08em' }}>{count} IN</span>
        </button>

        {onReplySend && !isOwn ? (
          <input
            value={replyValue}
            onChange={(e) => onReplyChange?.(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && replyValue.trim()) { onReplySend(); e.currentTarget.blur(); } }}
            onFocus={() => onReplyFocus?.(true)}
            onBlur={() => onReplyFocus?.(false)}
            placeholder={`Reply to ${replyTo}…`}
            aria-label={`Reply to ${replyTo}`}
            data-no-tap
            className="min-w-0 flex-1 text-white placeholder-white/60 focus:outline-none"
            style={{ height: 44, padding: '0 16px', background: 'rgba(0,0,0,.72)', border: 0, borderRadius: 999, fontFamily: 'inherit', fontSize: 12, boxShadow: 'none', caretColor: '#00FFFF' }}
          />
        ) : (
          <span className="min-w-0 flex-1" aria-hidden />
        )}

        {done && !action.inverted && !isOwn ? null : null}
        {button}
      </div>
    </>
  );
}
